import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { Text, TextType } from 'ui-components/components/text';

import { LOCAL_DELIVERY } from '../../../enums/csw-types';
import { Providers } from '../../../enums/providers';
import { EventSourceStrings } from '../../../tracking/tracking-constants';
import { useFulfillmentPlannerContext } from '../../../fulfillment-planner/context';
import { createAndPrintLabel } from '../../../store/csw/primary-actions/local-delivery/create-and-print-label';
import { createTestLabel } from '../../../store/csw/primary-actions/standard/create-test-label';
import * as Actions from '../../../store/scan-to-print/s2p-purchase-and-print.actions';
import { createTestLabel as createTestLabelAction } from '../../../store/csw/csw.actions';
import { useTrackLabelCreationStartFunc } from '../../../store/labels/tracking-events';
import { useContainsDemoOrder } from '../../../demos/hooks/use-contains-demo-order';
import Global from '../../../store/global-selectors';
import { Segment } from '../../../services/analytics-services/segment';
import { TEST_LABEL_BUTTON_CLICKED } from '../../../constants/analytics-events';

import { messages } from '../../messages';
import { PrintButton } from './index';
import { LabelPurchaseButton } from '../../../components/label-purchase-button';

const tooltipMessages = defineMessages({
  disabledTooltip: {
    description: 'tooltip message for when the print button is disabled',
    defaultMessage: 'Please Select any USPS service to enable Test Label',
    id: 'jbC8nX',
  },
});

export const PrintButtonContainer = ({ fulfillmentPlanId }) => {
  const dispatch = useDispatch();
  const { cswTypeId } = useFulfillmentPlannerContext();
  const isDemoOrder = useContainsDemoOrder([fulfillmentPlanId]);
  const sellerId = useSelector(Global.selectSellerSellerId);

  // Check if USPS is selected for demo orders
  const selectedSellerLabelProviderId = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'labelConfiguration.sellerLabelProviderId',
    }),
  );
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const uspsSellerLabelProviderId = sellerLabelProviders.find(
    (sellerLabelProvider) => sellerLabelProvider.labelProviderId === Providers.Stamps.labelProviderId,
  )?.sellerLabelProviderId;
  const isUspsSelected = selectedSellerLabelProviderId === uspsSellerLabelProviderId;
  const testLabelShouldBeDisabled = !isUspsSelected && isDemoOrder && selectedSellerLabelProviderId !== null;

  let message;
  if (isDemoOrder) {
    message = createTestLabel.translation;
  } else if (cswTypeId === LOCAL_DELIVERY) {
    message = createAndPrintLabel.translation;
  } else {
    message = messages.scanPrintButton;
  }

  const eventTriggerSource = 'Create Label button-scan to print';
  const trackLabelCreationStart = useTrackLabelCreationStartFunc([fulfillmentPlanId], eventTriggerSource);

  const onPrint = useCallback(() => {
    // Track test label button click with Segment if it's a test label
    if (isDemoOrder) {
      Segment.trackEvent(TEST_LABEL_BUTTON_CLICKED, {
        source: EventSourceStrings.SCAN_TO_PRINT,
        sellerId,
        fulfillmentPlanId,
      });
      dispatch(createTestLabelAction(fulfillmentPlanId));
    } else {
      dispatch(Actions.startPurchaseAndPrintJob(fulfillmentPlanId));
    }
    trackLabelCreationStart();
  }, [dispatch, fulfillmentPlanId, isDemoOrder, trackLabelCreationStart, sellerId]);

  // Create tooltip message if button should be disabled
  const disabledTooltipMessage = testLabelShouldBeDisabled ? (
    <Text textType={TextType.Caption} value={tooltipMessages.disabledTooltip} />
  ) : null;

  return (
    <LabelPurchaseButton fulfillmentPlanIds={[fulfillmentPlanId]} labelPurchaseCallback={onPrint} trackingId={eventTriggerSource}>
      {({ onClick }) => (
        <PrintButton
          disabled={testLabelShouldBeDisabled}
          disabledTooltipMessage={disabledTooltipMessage}
          message={message}
          onPrint={onClick}
        />
      )}
    </LabelPurchaseButton>
  );
};

PrintButtonContainer.propTypes = {
  fulfillmentPlanId: PropTypes.string.isRequired,
};
